/**
 * An in-app link that is a real `<a href>` — so a crawler can follow it and a
 * prerendered page carries working links — but navigates without a full reload
 * on a plain left click. Modified clicks (new tab, download) fall through to
 * the browser.
 *
 * `to` is written without a language prefix; the href is rendered in whatever
 * language the visitor is currently in, so every link stays inside that
 * language's copy of the site.
 */
import type { AnchorHTMLAttributes } from "react";
import { localize, navigate, useUrlLocale } from "@/lib/router";

interface LinkProps extends AnchorHTMLAttributes<HTMLAnchorElement> {
  to: string;
  replace?: boolean;
}

export function Link({ to, replace, onClick, ...rest }: LinkProps) {
  const locale = useUrlLocale();
  return (
    <a
      href={localize(to, locale)}
      onClick={(e) => {
        onClick?.(e);
        if (
          e.defaultPrevented ||
          e.button !== 0 ||
          e.metaKey ||
          e.ctrlKey ||
          e.shiftKey ||
          e.altKey
        ) {
          return;
        }
        e.preventDefault();
        navigate(to, replace);
      }}
      {...rest}
    />
  );
}
